import { useState, useRef } from "react";
import { api } from "../lib/api";
import { UploadSimple, X, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
}

export default function ImageUploader({ value = [], onChange, max = 2 }) {
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (files) => {
    const list = Array.from(files).slice(0, max - value.length);
    if (list.length === 0) { toast.error(`Maximum ${max} images`); return; }
    setBusy(true);
    const uploaded = [...value];
    for (const f of list) {
      if (!f.type.startsWith("image/")) { toast.error(`${f.name} is not an image`); continue; }
      if (f.size > 8 * 1024 * 1024) { toast.error(`${f.name} exceeds 8MB`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.url);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    onChange(uploaded);
    setBusy(false);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    if (e.dataTransfer.files?.length) upload(e.dataTransfer.files);
  };

  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const canAdd = value.length < max;

  return (
    <div data-testid="image-uploader">
      <div className="overline mb-2">Product images <span className="text-[#5C5C5C] normal-case tracking-normal">— {value.length}/{max}</span></div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        {value.map((src, i) => (
          <div key={i} className="relative group aspect-[3/4] bg-[#F0EBE1]" data-testid={`preview-${i}`}>
            <img src={resolveUrl(src)} alt={`Image ${i+1}`} className="absolute inset-0 w-full h-full object-cover" />
            <button type="button" onClick={() => removeAt(i)} className="absolute top-2 right-2 w-7 h-7 bg-[#1A1A1A]/80 text-[#FAF8F5] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`} aria-label="Remove">
              <X size={14} weight="light" />
            </button>
            <div className="absolute bottom-2 left-2 overline text-[10px] bg-[#FAF8F5]/90 px-2 py-0.5">{i === 0 ? "Primary" : "Hover"}</div>
          </div>
        ))}
        {canAdd && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={onDrop}
            className={`aspect-[3/4] border-2 border-dashed flex flex-col items-center justify-center gap-2 transition-colors ${drag ? "border-[#4A1525] bg-[#F0EBE1]" : "border-[#E6DED5] hover:border-[#1A1A1A]"}`}
            data-testid="upload-dropzone"
            disabled={busy}
          >
            {busy ? <Spinner size={28} weight="light" className="animate-spin" /> : <UploadSimple size={28} weight="light" />}
            <span className="overline text-[10px] text-[#5C5C5C] text-center px-2">
              {busy ? "Uploading..." : drag ? "Release to upload" : "Drop or click"}
            </span>
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => e.target.files && upload(e.target.files)} data-testid="upload-input" />
      <p className="text-[10px] text-[#5C5C5C]">JPG, PNG, WebP up to 8MB. Primary image shown on cards; second image reveals on hover.</p>
    </div>
  );
}
